/**
 * HomepageSectionsEditor — Per-section layout + product-card override
 * editor for the customer homepage. Each product row (Best Sellers,
 * Flash Deals, Trending, etc.) can independently choose:
 *   • its layout mode (Auto/Grid/Horizontal/Vertical/Slider/Auto-Carousel)
 *   • responsive columns, gap, autoplay, arrows, dots
 *   • its own Product Card sub-element overrides (button text/variant,
 *     discount tag position, delivery badge, etc.) — completely
 *     independent from every other section.
 *
 * Writes to `blob.homepage.sections[i]` — the same legacy consumer path
 * (`useStore().homepageSections`) that Home.jsx already reads. This
 * means zero migration risk and instant compatibility with the existing
 * runtime.
 *
 * Business logic (price, stock, discount %) is NEVER editable here.
 */
import { useMemo, useState } from "react";
import { X, ChevronDown, ChevronRight, Sparkles, LayoutTemplate, Package, GripVertical, Monitor, Tablet, Smartphone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import * as LucideIcons from "lucide-react";
import { CATEGORY_LAYOUTS, LAYOUT_DEFAULTS, CATEGORY_LAYOUT_FIELDS } from "@/lib/visualBuilder/categoryLayouts";
import { PRODUCT_CARD_SCHEMA, productCardFieldKeys } from "@/lib/visualBuilder/productCardSchema";
import { moveItem } from "@/lib/visualBuilder/layoutTree";
import ProductCardSample from "./ProductCardSample";

const CARD_KEYS = productCardFieldKeys();

// Sections whose renderer uses ProductRail / AllProductsGrid — i.e. the
// only ones where a card_override makes sense.
const PRODUCT_SECTION_KEYS = new Set([
  "flash_deals", "trending", "recently_viewed", "buy_again",
  "best_sellers", "recommended", "all_products",
]);

export default function HomepageSectionsEditor({ homepage, baseCard, onChange, onClose }) {
  const sections = Array.isArray(homepage?.sections) ? homepage.sections : [];
  const [openId, setOpenId] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);

  const setSection = (idx, patch) => {
    const next = sections.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange({ homepage: { ...(homepage || {}), sections: next } });
  };
  const reorder = (from, to) => {
    onChange({ homepage: { ...(homepage || {}), sections: moveItem(sections, from, to) } });
  };
  const onRowDragStart = (idx) => (e) => {
    setDragIdx(idx);
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/vb-hs", String(idx)); } catch { /* noop */ }
  };
  const onRowDragOver = (idx) => (e) => { e.preventDefault(); setDropIdx(idx); };
  const onRowDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIdx; setDragIdx(null); setDropIdx(null);
    if (from == null || from === idx) return;
    reorder(from, idx);
  };
  const onRowDragEnd = () => { setDragIdx(null); setDropIdx(null); };
  const setSectionCardOverride = (idx, patch) => {
    const s = sections[idx] || {};
    const next = { ...(s.card_override || {}) };
    // Whitelist against known card keys — never write unknown/business keys.
    // A value of `undefined` DELETES the override for that key (revert).
    Object.entries(patch).forEach(([k, v]) => {
      if (!CARD_KEYS.has(k)) return;
      if (v === undefined) delete next[k];
      else next[k] = v;
    });
    setSection(idx, { card_override: next });
  };
  const clearOverride = (idx) => setSection(idx, { card_override: {} });

  return (
    <div className="p-4 space-y-4" data-testid="vb-homepage-editor">
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Homepage · Per-Section</div>
          <div className="text-[15px] font-semibold text-slate-900 truncate">Section Layout &amp; Card</div>
          <div className="text-[10.5px] text-slate-400">Each product row can pick its own layout + card variant.</div>
        </div>
        <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100" data-testid="vb-hs-close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {sections.length === 0 && (
        <div className="text-center text-[12px] text-slate-400 py-6 rounded-xl border border-dashed border-slate-200">
          No homepage sections found. Use the legacy Website Builder to seed them first.
        </div>
      )}

      <div className="space-y-2">
        {sections.map((s, idx) => {
          const isOpen = openId === s.key;
          const isProduct = PRODUCT_SECTION_KEYS.has(s.key);
          const enabled = s.enabled !== false;
          const locked = !!s.locked;
          const isDropTarget = dropIdx === idx && dragIdx !== null && dragIdx !== idx;
          return (
            <div
              key={s.key}
              className={`rounded-xl border bg-white overflow-hidden transition-shadow ${
                isDropTarget ? "border-[#E32636] ring-2 ring-[#E32636]/20" : "border-slate-200"
              } ${dragIdx === idx ? "opacity-40" : ""}`}
              draggable={!locked}
              onDragStart={onRowDragStart(idx)}
              onDragOver={onRowDragOver(idx)}
              onDrop={onRowDrop(idx)}
              onDragEnd={onRowDragEnd}
              data-testid={`vb-hs-sec-${s.key}`}
            >
              <div
                className={`flex items-center gap-2 px-3 py-2.5 text-left ${isOpen ? "bg-[#FFF3F4]" : "hover:bg-slate-50"}`}
              >
                <GripVertical className={`w-3.5 h-3.5 shrink-0 cursor-grab ${locked ? "text-slate-200" : "text-slate-400"}`} title={locked ? "Locked" : "Drag to reorder"} data-testid={`vb-hs-drag-${s.key}`} />
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : s.key)}
                  className="flex-1 min-w-0 flex items-center gap-2 text-left"
                  data-testid={`vb-hs-toggle-${s.key}`}
                >
                  <div className={`w-7 h-7 rounded-md grid place-items-center shrink-0 ${isOpen ? "bg-[#E32636] text-white" : "bg-slate-100 text-slate-600"}`}>
                    {isProduct ? <Package className="w-3.5 h-3.5" /> : <LayoutTemplate className="w-3.5 h-3.5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold text-slate-900 truncate">{s.label || s.key}</div>
                    <div className="text-[10px] font-mono text-slate-400 truncate">{s.key}{s.layout ? ` · ${s.layout}` : ""}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                    {enabled ? "ON" : "OFF"}
                  </span>
                  {isOpen
                    ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                    : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                </button>
              </div>

              {isOpen && (
                <div className="p-3 space-y-4 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <Switch checked={enabled} onCheckedChange={(v) => setSection(idx, { enabled: v })} data-testid={`vb-hs-enabled-${s.key}`} />
                    <span className="text-[11.5px] text-slate-600">{enabled ? "Visible to customers" : "Hidden"}</span>
                  </div>

                  {isProduct && (
                    <>
                      <LayoutBlock section={s} onChange={(patch) => setSection(idx, patch)} />
                      <CardOverrideBlock
                        section={s} baseCard={baseCard || {}}
                        onChange={(patch) => setSectionCardOverride(idx, patch)}
                        onClear={() => clearOverride(idx)}
                      />
                    </>
                  )}

                  {!isProduct && (
                    <div className="text-[11px] text-slate-400 rounded-lg border border-slate-100 bg-slate-50 p-2.5 leading-relaxed">
                      Non-product sections (hero, banners, footer) don't use the Product Card — layout &amp; card overrides are only available for product rows.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="pt-2 text-[10.5px] text-slate-400 border-t border-slate-100 leading-relaxed">
        Prices, stock, and discounts stay untouched — only visual configuration is stored here.
      </div>
    </div>
  );
}

/* ─── Sub-blocks ────────────────────────────────────────────────────── */

function LayoutBlock({ section, onChange }) {
  const v = { ...LAYOUT_DEFAULTS, ...section };
  return (
    <div className="space-y-2">
      <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold">Layout</div>
      <div className="grid grid-cols-3 gap-2">
        {CATEGORY_LAYOUTS.map((L) => {
          const Icon = LucideIcons[L.icon] || LayoutTemplate;
          const active = (v.layout || "auto") === L.value;
          return (
            <button
              key={L.value}
              type="button"
              onClick={() => onChange({ layout: L.value })}
              className={`flex flex-col items-center gap-1 h-16 rounded-xl border p-1.5 transition-all ${
                active ? "border-[#E32636] bg-[#FFF3F4] shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
              }`}
              title={L.hint}
              data-testid={`vb-hs-layout-${section.key}-${L.value}`}
            >
              <Icon className={`w-4 h-4 ${active ? "text-[#E32636]" : "text-slate-500"}`} />
              <span className={`text-[10px] font-semibold ${active ? "text-[#E32636]" : "text-slate-700"}`}>{L.label}</span>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-2 pt-1">
        {CATEGORY_LAYOUT_FIELDS.map((f) => {
          if (f.showIfLayout && !f.showIfLayout.includes(v.layout || "auto")) return null;
          return (
            <div key={f.key} className={f.type === "toggle" ? "col-span-2" : "col-span-1"}>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{f.label}</Label>
              {f.type === "number" && (
                <Input
                  type="number"
                  value={v[f.key] ?? ""}
                  onChange={(e) => onChange({ [f.key]: e.target.value === "" ? undefined : Number(e.target.value) })}
                  min={f.min} max={f.max} step={f.step}
                  placeholder={String(LAYOUT_DEFAULTS[f.key] ?? "")}
                  className="h-8 text-[12px]"
                  data-testid={`vb-hs-field-${section.key}-${f.key}`}
                />
              )}
              {f.type === "toggle" && (
                <div className="flex items-center gap-2 h-8">
                  <Switch
                    checked={v[f.key] !== false && !!v[f.key]}
                    onCheckedChange={(val) => onChange({ [f.key]: val })}
                    data-testid={`vb-hs-field-${section.key}-${f.key}`}
                  />
                  <span className="text-[11px] text-slate-500">{v[f.key] ? "On" : "Off"}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CardOverrideBlock({ section, baseCard, onChange, onClear }) {
  const override = section.card_override || {};
  const hasOverride = Object.keys(override).length > 0;
  const merged = useMemo(() => ({ ...baseCard, ...override }), [baseCard, override]);
  const [openField, setOpenField] = useState(null);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold">Card Override</div>
        {hasOverride && (
          <button
            type="button" onClick={onClear}
            className="text-[10px] font-semibold text-red-500 hover:text-red-700"
            data-testid={`vb-hs-clear-${section.key}`}
          >Clear override</button>
        )}
      </div>
      <div className="flex flex-col items-center bg-slate-100 rounded-xl p-2 gap-2">
        <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">
          {hasOverride ? "This section (overridden)" : "Uses base card"}
        </div>
        <ProductCardSample card={merged} testId={`vb-hs-preview-${section.key}`} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        {PRODUCT_CARD_SCHEMA.map((sec) => (
          <button
            key={sec.id}
            type="button"
            onClick={() => setOpenField(openField === sec.id ? null : sec.id)}
            className={`h-8 rounded-lg border text-[11px] font-semibold text-left px-2 truncate ${
              openField === sec.id ? "border-[#E32636] bg-[#FFF3F4] text-[#E32636]" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
            }`}
            data-testid={`vb-hs-sub-${section.key}-${sec.id}`}
          >{sec.title}</button>
        ))}
      </div>

      {openField && (
        <div className="rounded-xl border border-slate-200 bg-white p-2.5 space-y-2 max-h-[280px] overflow-y-auto">
          {(PRODUCT_CARD_SCHEMA.find((s) => s.id === openField)?.fields || []).map((f) => (
            <FieldRow
              key={f.key} field={f}
              value={override[f.key] ?? undefined}
              placeholder={baseCard[f.key]}
              onChange={(v) => onChange({ [f.key]: v })}
              onRevert={() => onChange({ [f.key]: undefined })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FieldRow({ field, value, placeholder, onChange, onRevert }) {
  const testId = `vb-hs-fld-${field.key}`;
  const wide = field.type === "text" || field.hint;
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{field.label}</Label>
        {value !== undefined && (
          <button type="button" onClick={onRevert} className="text-[9.5px] text-slate-400 hover:text-red-500" data-testid={`${testId}-revert`}>Revert</button>
        )}
      </div>
      {field.type === "text" && (
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={String(placeholder ?? "")} className="h-8 text-[12px]" data-testid={testId} />
      )}
      {field.type === "number" && (
        <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))} placeholder={String(placeholder ?? "")} min={field.min} max={field.max} step={field.step || 1} className="h-8 text-[12px]" data-testid={testId} />
      )}
      {field.type === "color" && (
        <div className="flex items-center gap-1.5">
          <input type="color" value={value || placeholder || "#ffffff"} onChange={(e) => onChange(e.target.value)} className="h-8 w-8 rounded-lg border border-slate-200 cursor-pointer" data-testid={`${testId}-picker`} />
          <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={String(placeholder ?? "auto")} className="h-8 text-[11px] font-mono flex-1" data-testid={testId} />
        </div>
      )}
      {field.type === "select" && (
        <select value={String(value ?? placeholder ?? "")} onChange={(e) => {
          const opt = field.options.find((o) => String(o.value) === e.target.value);
          onChange(opt ? opt.value : e.target.value);
        }} className="h-8 w-full text-[12px] rounded-md border border-slate-200 px-2 bg-white" data-testid={testId}>
          {field.options.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
        </select>
      )}
      {field.type === "toggle" && (
        <div className="flex items-center gap-2 h-8">
          <Switch checked={value !== undefined ? !!value : !!placeholder} onCheckedChange={(v) => onChange(v)} data-testid={testId} />
          <span className="text-[11px] text-slate-500">{(value !== undefined ? value : placeholder) ? "On" : "Off"}</span>
        </div>
      )}
      {field.hint && <div className="text-[9.5px] text-slate-400 mt-0.5">{field.hint}</div>}
    </div>
  );
}
